import { ReactElement, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  type Notification,
  type NotificationCounts,
  useInfiniteNotifications,
} from '@hooks/notifications';
import { Popover } from '@mui/material';

import {
  getNotificationResourcePath,
  isUnread,
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationsTab,
} from './notifications.utils.js';
import { NotificationsPanel } from './NotificationsPanel.js';

const UNREAD_FILTERS = { isRead: false };

interface NotificationsPopoverProps {
  anchorEl: HTMLElement | null;
  counts: NotificationCounts;
  markAllAsRead: () => unknown;
  markAsRead: (variables: { notificationId: string }) => unknown;
  onClose: () => void;
}

export function NotificationsPopover({
  anchorEl,
  counts,
  markAllAsRead,
  markAsRead,
  onClose,
}: NotificationsPopoverProps): ReactElement {
  const history = useHistory();
  const [tab, setTab] = useState<NotificationsTab>('all');
  const open = Boolean(anchorEl);

  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetching,
    isFetchingNextPage,
    normalizedData,
    refetch,
    sentinelRef,
  } = useInfiniteNotifications({
    enabled: open,
    filters: tab === 'unread' ? UNREAD_FILTERS : undefined,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
  });

  const handleRetry = useCallback(() => {
    if (isFetchNextPageError) fetchNextPage();
    else refetch();
  }, [fetchNextPage, isFetchNextPageError, refetch]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (isUnread(notification)) markAsRead({ notificationId: notification.id });
      const path = getNotificationResourcePath(notification);
      if (!path) return;
      onClose();
      history.push(path);
    },
    [history, markAsRead, onClose]
  );

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={open}
      slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 32px)', sm: 420 } } } }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={onClose}
    >
      <NotificationsPanel
        counts={counts}
        hasError={Boolean(error)}
        hasNextPage={hasNextPage}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        notifications={normalizedData}
        sentinelRef={sentinelRef}
        tab={tab}
        onMarkAllAsRead={handleMarkAllAsRead}
        onNotificationClick={handleNotificationClick}
        onRetry={handleRetry}
        onTabChange={setTab}
      />
    </Popover>
  );
}
